import { GridColDef, GridFilterModel, GridLinkOperator, useGridApiRef } from '@mui/x-data-grid-pro';
import { ReactNode, useEffect, useState } from 'react';
import { useToggleLeadSprint } from '../../../../hooks/sprint-leads.hooks';
import { LeadGridItem } from '../../../../types/Lead';
import { Filter, FilterType } from '../../../filters/FilterTypes';
import LeadDataGridView2 from '../../../Components/LeadDataGridView2';
import Box from '@mui/material/Box';
import WizardStep from '../../../../components/Wizard/WizardStep';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import isEmpty from 'lodash/isEmpty';
import { Sprint } from "../../../../stores/SprintStore";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import useICPWizard from '../hooks/useICPWizard';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { useTheme } from '@mui/material';
import React from 'react';

type Props = {    
    steps?: ReactNode,
    onClose: () => void,
}

const ICPAssignLeadList = ({ onClose, steps }: Props) => {
    const [pageSize, setPageSize] = useState(10)
    const [selectedLeadId, setSelectedLeadId] = useState('');
    const [unassignedOnly, setUnassignedOnly] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { useGetIcpLeads, icp, onICPChange, resetLeads, sprint } = useICPWizard();
    const { data: paginatedLeads, isLoading, remove, fetchNextPage, hasNextPage, refetch } = useGetIcpLeads(icp.primaryFilters.options, icp.primaryFilters.searchTerm, pageSize, unassignedOnly, sprint.id);
    const { mutateAsync } = useToggleLeadSprint();
    const apiRef = useGridApiRef();
    const theme = useTheme();

    useEffect(() => {
        if (!isLoading) {
            resetLeads(icp.primaryFilters.options, icp.primaryFilters.searchTerm, pageSize, unassignedOnly, sprint.id);
            refetch({ refetchPage: (page, index) => index === 0 })
        }
    }, [icp])

    const beginToggleSprint = async (leadId: string, currentSprintId: string) => {
        if (currentSprintId !== sprint.id && currentSprintId) {
            setSelectedLeadId(leadId);
            setConfirmOpen(true);
            return;
        }

        await toggleSprint(leadId);
    }

    const toggleSprint = async (leadId: string) => {
        var result = await mutateAsync({
            leadId: leadId,
            sprintId: sprint.id,
            cadenceId: icp.cadenceId,
            cadenceStartDate: icp.cadenceStartDate,
            ownerId: icp.ownerId
        });

        refetch();
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnassignedOnly(event.target.checked)
    };

    const preColumns = [
        {
            flex: 1,
            minWidth: 120,
            field: '',
            headerName: 'Assigned',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography variant='body2' noWrap alignItems='center' display='flex' flexDirection='row'>
                        <Checkbox checked={!isEmpty(row.sprintName) && row.sprintId === sprint.id} onClick={() => beginToggleSprint(row.id, row.sprintId)} />
                        {row.sprintId !== sprint.id && row.sprintId && <Box component='span' sx={{ cursor: 'pointer' }} title={`Lead is already assigned to sprint ${row.sprintName}`}><WarningAmberIcon color='warning' /></Box>}
                    </Typography>
                )
            }
        }
    ] as GridColDef[];

    const handleGridStateChange = () => {
        //const state = apiRef.current.exportState();

        //if (!state.filter || !state.filter.filterModel) return;

        //onICPChange({
        //    ...icp,
        //    refinedFilters: state.filter.filterModel.items,
        //    refinedFiltersLinkOperator: state.filter.filterModel.linkOperator ?? GridLinkOperator.And
        //});
    }

    const onFilterModelChange = React.useCallback((filterModel: GridFilterModel) => {
        onICPChange({
            ...icp,
            refinedFilters: filterModel.items,
            refinedFiltersLinkOperator: filterModel.linkOperator ?? GridLinkOperator.And
        });
    }, []);

    return (
        <WizardStep
            title={`Assign Leads - ${sprint.name}`}
            subtitle={`Using the grid below you can assign leads to sprint. You can also select a pre-configured ideal customer profile using the dropdown.`}
            onFinish={onClose}
            onCancel={onClose}
        >
            <Box display='flex' sx={{ height: '100%', width: '100%' }}>
                {steps}
                <Box display='flex' flexDirection='column' sx={{ height: '100%', width: '100%' }}>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <FormControl component="fieldset" variant="standard">
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch checked={unassignedOnly} onChange={handleChange} name="gilad" />
                                    }
                                    label="Unassigned only"
                                />
                            </FormGroup>
                        </FormControl>
                    </Box>

                    {isLoading && <LoadingSpinner text="Fetching Leads" />}
                    {!isLoading && <LeadDataGridView2
                        onFilterModelChange={onFilterModelChange}
                        gridFilterItems={icp.refinedFilters}
                        gridLinkOperator={icp.refinedFiltersLinkOperator}
                        onGridStateChange={handleGridStateChange}
                        apiRef={apiRef}
                        isLoading={isLoading}
                        leadPages={paginatedLeads?.pages}
                        selectedLeadIds={[]}
                        hasNextPage={hasNextPage === true}
                        fetchNextPage={fetchNextPage}
                        preColumns={preColumns}
                        onSelectionModeChange={() => { }}
                    />}
                </Box>
            </Box>
            <ConfirmationDialog
                onConfirm={() => toggleSprint(selectedLeadId)}
                onCancel={() => { setSelectedLeadId(''); setConfirmOpen(false) }}
                open={confirmOpen}
                title="Warning"
                message="The lead is currently active on another sprint. Re-assiging the lead will reset its cadence, current activity and next activity date. Are you sure you wish to re-assign this lead? " />
        </WizardStep>
    )
}

interface CellType {
    row: LeadGridItem
}

export { ICPAssignLeadList };
export default ICPAssignLeadList;